import { useState } from "react";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useNavigate, Link } from "react-router-dom";
import { Target, Zap, TrendingUp, Users, CheckCircle, ArrowRight, Loader2, LogIn, FileText, BarChart3, MessageSquare, CheckSquare, Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { AnimatedLogo } from "@/components/ui/animated-logo";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { AIAssistant } from "@/components/ai-assistant/AIAssistant";
import { CaseStudies } from "@/components/landing/CaseStudies";
import SEOHead from "@/components/seo/SEOHead";
import image1 from "@/assets/1.jpeg";
import image2 from "@/assets/2.jpeg";
import image3 from "@/assets/3.jpeg";
import image4 from "@/assets/4.jpeg";
import image5 from "@/assets/5.jpeg";
import image6 from "@/assets/6.jpeg";
import image7 from "@/assets/7.jpeg";

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    serviceType: "",
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    description: "",
    budgetRange: "",
    preferredStartDate: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to request a service.",
      });
      navigate("/client-login");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.from("service_requests").insert({
        client_id: user.id,
        service_type: formData.serviceType,
        business_name: formData.businessName,
        contact_name: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        description: formData.description,
        budget_range: formData.budgetRange,
        preferred_start_date: formData.preferredStartDate || null,
      });

      if (error) throw error;

      toast({
        title: "Request submitted!",
        description: "We'll get back to you within 24 hours.",
      });

      navigate("/client-portal");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const services = [
    "Social Media Marketing",
    "SEO Optimization",
    "Content Creation",
    "Paid Advertising",
    "Brand Strategy",
    "Web Development",
    "Graphic Design",
    "Video Production",
  ];

  const features = [
    {
      icon: Target,
      title: t("landing.features.strategic"),
      description: t("landing.features.strategicDesc")
    },
    {
      icon: Zap,
      title: t("landing.features.fast"),
      description: t("landing.features.fastDesc")
    },
    {
      icon: TrendingUp,
      title: t("landing.features.growth"),
      description: t("landing.features.growthDesc")
    },
    {
      icon: Users,
      title: t("landing.features.support"),
      description: t("landing.features.supportDesc")
    }
  ];

  const stats = [
    { value: "200+", label: t("landing.stats.clients") },
    { value: "500+", label: t("landing.stats.projects") },
    { value: "98%", label: t("landing.stats.satisfaction") },
    { value: "24/7", label: t("landing.stats.support") }
  ];

  const platformFeatures = [
    {
      icon: LogIn,
      image: image7,  // "Dashboard Overview"
      title: t("landing.platform.dashboard"),
      description: t("landing.platform.dashboardDesc"),
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: FileText,
      image: image6,  // "My Projects"
      title: t("landing.platform.projects"),
      description: t("landing.platform.projectsDesc"),
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: CheckSquare,
      image: image5,  // "Service Requests" (My Requests)
      title: t("landing.platform.requests"),
      description: t("landing.platform.requestsDesc"),
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: Target,
      image: image4,  // "New Service Request"
      title: t("landing.platform.newRequest"),
      description: t("landing.platform.newRequestDesc"),
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: FileText,
      image: image3,  // "Dashboard" (Invoices)
      title: t("landing.platform.invoices"),
      description: t("landing.platform.invoicesDesc"),
      color: "from-rose-500 to-pink-500"
    },
    {
      icon: MessageSquare,
      image: image2,  // "Support Chat"
      title: t("landing.platform.chat"),
      description: t("landing.platform.chatDesc"),
      color: "from-violet-500 to-purple-500"
    },
    {
      icon: BarChart3,
      image: image1,  // "Analytics & Insights"
      title: t("landing.platform.analytics"),
      description: t("landing.platform.analyticsDesc"),
      color: "from-purple-500 to-fuchsia-500"
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden" itemScope itemType="https://schema.org/WebPage">
      {/* SEO Head */}
      <SEOHead
        title="Morocco's #1 Digital Marketing Agency"
        description="SpotlightAdz is Morocco's leading digital marketing agency. Expert services in SEO, social media marketing, paid advertising, content creation, and web development. Grow your business with data-driven strategies."
        keywords="SpotlightAdz, digital marketing agency Morocco, SEO Morocco, social media marketing, paid advertising, web development, spotlightadz.ma, spotlightadz.com"
        canonicalUrl="https://spotlightadz.ma"
      />
      
      <AIAssistant context="landing" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 glass-card backdrop-blur-xl">
        <div className="responsive-container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AnimatedLogo size={36} />
              <span className="font-bold text-2xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" dir={language === "ar" ? "rtl" : "ltr"}>Spotlight Adz</span>
            </div>
              <nav className="hidden md:flex items-center gap-8">
                <a href="#case-studies" className="text-sm font-medium hover:text-primary transition-colors">Case Studies</a>
                <Link to="/blog" className="text-sm font-medium hover:text-primary transition-colors">Blog</Link>
                <a href="#services" className="text-sm font-medium hover:text-primary transition-colors">{t("nav.services")}</a>
                <a href="#platform" className="text-sm font-medium hover:text-primary transition-colors">{t("nav.platform")}</a>
                <a href="#stats" className="text-sm font-medium hover:text-primary transition-colors">Results</a>
              <a href="#request" className="text-sm font-medium hover:text-primary transition-colors">{t("nav.getStarted")}</a>
            </nav>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              {user ? (
                <Button className="btn-primary gap-2" onClick={() => navigate('/client-portal')}>
                  <BarChart3 className="h-4 w-4" />
                  {t("nav.myPortal")}
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/client-login')}>
                    <LogIn className="h-4 w-4 mr-2" />
                    {t("nav.signIn")}
                  </Button>
                  <Button className="btn-primary hidden sm:flex" onClick={() => {
                    const form = document.getElementById('service-request-form');
                    form?.scrollIntoView({ behavior: 'smooth' });
                  }}>
                    {t("nav.getStarted")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 responsive-container pt-32 pb-20 md:pt-40 md:pb-32" dir={language === "ar" ? "rtl" : "ltr"} role="banner">
        <div className="text-center max-w-5xl mx-auto space-y-8 animate-fade-in">
          <div className="inline-flex items-center gap-2 glass-card px-4 py-2 text-sm mb-6">
            <Zap className="h-4 w-4 text-primary" />
            <span className="text-primary font-medium">{t("landing.hero.partner")}</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight">
            <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              {t("landing.hero.title")}
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t("landing.hero.description")}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Button 
              size="lg" 
              className="btn-primary text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all" 
              onClick={() => {
                const form = document.getElementById('service-request-form');
                form?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t("landing.hero.requestService")}
              <ArrowRight className={language === "ar" ? "mr-2 h-5 w-5 rotate-180" : "ml-2 h-5 w-5"} />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="btn-glass text-lg px-8 py-6"
              onClick={() => navigate('/client-login')}
            >
              <FileText className="h-5 w-5 mr-2" />
              View Case Studies
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-8 pt-8 opacity-60">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              <span className="text-sm">No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              <span className="text-sm">24/7 Support</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              <span className="text-sm">Cancel Anytime</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="services" className="relative z-10 responsive-container py-20" dir={language === "ar" ? "rtl" : "ltr"} aria-label="Our Services">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 glass-card px-4 py-2 text-sm text-muted-foreground mb-6">
            <TrendingUp className="h-4 w-4" />
            <span>{t("landing.features.badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t("landing.features.title")}</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t("landing.features.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="glass-card-hover p-8 group">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <feature.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works - Platform Guide */}
      <section id="platform" className="relative z-10 responsive-container py-20" dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 glass-card px-4 py-2 text-sm text-muted-foreground mb-6">
            <CheckSquare className="h-4 w-4" />
            <span>{t("landing.platform.badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t("landing.platform.title")}</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t("landing.platform.subtitle")}
          </p>
        </div>

        <div className="space-y-24">
          {platformFeatures.map((step, index) => (
            <div 
              key={index} 
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} text-white shadow-lg`}>
                  <step.icon className="h-8 w-8" />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br ${step.color} text-white text-sm font-bold`}>
                      {index + 1}
                    </div>
                    <h3 className="text-3xl font-bold">{step.title}</h3>
                  </div>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  {index === 0 && (
                    <>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.quickSetup")}</span>
                      </div>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.noCreditCard")}</span>
                      </div>
                    </>
                  )}
                  {index === 1 && (
                    <>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.realTimeUpdates")}</span>
                      </div>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.fileManagement")}</span>
                      </div>
                    </>
                  )}
                  {index === 2 && (
                    <>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.directMessaging")}</span>
                      </div>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.meetingScheduler")}</span>
                      </div>
                    </>
                  )}
                  {index === 3 && (
                    <>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.performanceMetrics")}</span>
                      </div>
                      <div className="flex items-center gap-2 glass-card px-4 py-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>{t("common.roiTracking")}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden glass-card p-2 group hover-scale">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <img 
                    src={step.image} 
                    alt={step.title}
                    className="w-full h-auto rounded-xl shadow-2xl animate-fade-in"
                  />
                  <div className={`absolute -z-10 inset-0 bg-gradient-to-br ${step.color} blur-3xl opacity-20 group-hover:opacity-30 transition-opacity`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Access CTA */}
        <div className="mt-20 text-center">
          <div className="glass-card p-8 md:p-12 inline-block">
            <h3 className="text-2xl font-bold mb-4">{t("common.readyToStart")}</h3>
            <p className="text-muted-foreground mb-6">
              {t("common.readyDesc")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="btn-primary"
                onClick={() => {
                  const form = document.getElementById('service-request-form');
                  form?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t("common.getStartedNow")}
                <ArrowRight className={language === "ar" ? "mr-2 h-5 w-5 rotate-180" : "ml-2 h-5 w-5"} />
              </Button>
              {user && (
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="btn-glass"
                  onClick={() => navigate('/client-portal')}
                >
                  {t("common.goToPortal")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <CaseStudies />


      {/* Service Request Form */}
      <section id="request" className="relative z-10 responsive-container py-20" dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 glass-card px-4 py-2 text-sm text-muted-foreground mb-6">
              <FileText className="h-4 w-4" />
              <span>{t("nav.getStarted")}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t("landing.form.title")}</h2>
            <p className="text-xl text-muted-foreground">
              {t("landing.form.subtitle")}
            </p>
          </div>

          <Card className="glass-card p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="serviceType">{t("landing.form.serviceType")} *</Label>
                <Select
                  value={formData.serviceType}
                  onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
                  required
                >
                  <SelectTrigger className="input-modern">
                    <SelectValue placeholder={t("landing.form.selectService")} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="businessName">{t("landing.form.businessName")} *</Label>
                  <Input
                    id="businessName"
                    className="input-modern"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactName">{t("landing.form.contactName")} *</Label>
                  <Input
                    id="contactName"
                    className="input-modern"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("landing.form.email")} *</Label>
                  <Input
                    id="email"
                    type="email"
                    className="input-modern"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t("landing.form.phone")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    className="input-modern"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t("landing.form.description")} *</Label>
                <Textarea
                  id="description"
                  rows={5}
                  className="input-modern resize-none"
                  placeholder={t("landing.form.descPlaceholder")}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="budgetRange">{t("landing.form.budget")}</Label>
                  <Select
                    value={formData.budgetRange}
                    onValueChange={(value) => setFormData({ ...formData, budgetRange: value })}
                  >
                    <SelectTrigger className="input-modern">
                      <SelectValue placeholder={t("landing.form.selectBudget")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="$500-$1,000">$500 - $1,000</SelectItem>
                      <SelectItem value="$1,000-$5,000">$1,000 - $5,000</SelectItem>
                      <SelectItem value="$5,000-$10,000">$5,000 - $10,000</SelectItem>
                      <SelectItem value="$10,000+">$10,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredStartDate">{t("landing.form.startDate")}</Label>
                  <Input
                    id="preferredStartDate"
                    type="date"
                    className="input-modern"
                    value={formData.preferredStartDate}
                    onChange={(e) => setFormData({ ...formData, preferredStartDate: e.target.value })}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary text-lg py-6" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className={language === "ar" ? "ml-2 h-5 w-5 animate-spin" : "mr-2 h-5 w-5 animate-spin"} />
                    {t("landing.form.submitting")}
                  </>
                ) : (
                  <>
                    {t("landing.form.submit")}
                    <ArrowRight className={language === "ar" ? "mr-2 h-5 w-5 rotate-180" : "ml-2 h-5 w-5"} />
                  </>
                )}
              </Button>

              {!user && (
                <p className="text-sm text-muted-foreground text-center">
                  Don't have an account?{" "}
                  <Button
                    variant="link"
                    className="p-0 h-auto font-semibold"
                    onClick={() => navigate("/client-login")}
                  >
                    Sign up here
                  </Button>
                </p>
              )}
            </form>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section id="stats" className="relative z-10 responsive-container py-20 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of successful businesses that trust Spotlight Adz for their digital growth
          </p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {stats.map((stat, index) => (
            <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 border-border/50 glass-card">
              <div className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-muted-foreground font-medium">{stat.label}</div>
            </Card>
          ))}
        </div>

        <div className="text-center">
          <Button 
            size="lg" 
            className="btn-primary text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all"
            onClick={() => {
              const form = document.getElementById('service-request-form');
              form?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Start Your Free Consultation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Modern Footer */}
      <footer className="relative z-10 border-t border-border/50 mt-20 bg-card/30 backdrop-blur-sm" role="contentinfo" itemScope itemType="https://schema.org/WPFooter">
        <div className="responsive-container py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="space-y-4" itemScope itemType="https://schema.org/Organization">
              <div className="flex items-center gap-3">
                <AnimatedLogo size={40} />
                <h4 className="font-bold text-xl text-foreground" itemProp="name">Spotlight Adz</h4>
              </div>
              <p className="text-muted-foreground leading-relaxed" itemProp="description">
                Leading digital marketing agency in Morocco, delivering data-driven solutions that drive real business growth.
              </p>
              <meta itemProp="url" content="https://spotlightadz.ma" />
              <div className="flex gap-4">
                <a href="https://facebook.com/spotlightadz" target="_blank" rel="noopener noreferrer" aria-label="SpotlightAdz on Facebook" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                  <Facebook className="h-5 w-5 text-primary" />
                </a>
                <a href="https://instagram.com/spotlightadz" target="_blank" rel="noopener noreferrer" aria-label="SpotlightAdz on Instagram" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                  <Instagram className="h-5 w-5 text-primary" />
                </a>
                <a href="https://linkedin.com/company/spotlightadz" target="_blank" rel="noopener noreferrer" aria-label="SpotlightAdz on LinkedIn" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                  <Linkedin className="h-5 w-5 text-primary" />
                </a>
                <a href="https://twitter.com/spotlightadz" target="_blank" rel="noopener noreferrer" aria-label="SpotlightAdz on Twitter" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                  <Twitter className="h-5 w-5 text-primary" />
                </a>
              </div>
            </div>

            {/* Services */}
            <nav aria-label="Services navigation">
              <h5 className="font-semibold text-foreground mb-4">Services</h5>
              <ul className="space-y-3">
                <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Social Media Marketing</a></li>
                <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">SEO Optimization</a></li>
                <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Content Creation</a></li>
                <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Paid Advertising</a></li>
                <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Brand Strategy</a></li>
                <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Web Development</a></li>
              </ul>
            </nav>

            {/* Company */}
            <nav aria-label="Company navigation">
              <h5 className="font-semibold text-foreground mb-4">Company</h5>
              <ul className="space-y-3">
                <li><a href="https://spotlightadz.ma" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">SpotlightAdz.ma</a></li>
                <li><a href="https://spotlightadz.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">SpotlightAdz.com</a></li>
                <li><Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">Blog</Link></li>
                <li><a href="#case-studies" className="text-muted-foreground hover:text-primary transition-colors">Case Studies</a></li>
                <li><Link to="/client-login" className="text-muted-foreground hover:text-primary transition-colors">Client Portal</Link></li>
              </ul>
            </nav>

            {/* Contact */}
            <div itemScope itemType="https://schema.org/ContactPoint">
              <h5 className="font-semibold text-foreground mb-4">Contact</h5>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <a href="mailto:contact@spotlightadz.ma" className="text-muted-foreground hover:text-primary transition-colors" itemProp="email">
                    contact@spotlightadz.ma
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <a href="tel:+212701177915" className="text-muted-foreground hover:text-primary transition-colors" itemProp="telephone">
                    +212 701 177 915
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground" itemProp="areaServed">
                    Martil, Morocco
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border/50">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
              <p>© {new Date().getFullYear()} SpotlightAdz. All rights reserved. | <a href="https://spotlightadz.ma" className="hover:text-primary transition-colors">spotlightadz.ma</a></p>
              <nav className="flex gap-6" aria-label="Legal navigation">
                <a href="https://spotlightadz.ma/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Privacy Policy</a>
                <a href="https://spotlightadz.ma/terms" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Terms of Service</a>
                <a href="https://spotlightadz.ma/cookies" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Cookie Policy</a>
              </nav>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
